// src/views/InvoiceDetail.jsx
import React, { useEffect, useState, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import './InvoiceDetail.css';
import EmailModal from '../components/EmailModal.jsx';
import { FaArrowLeft, FaEnvelope, FaSyncAlt, FaFilePdf, FaPaperclip, FaUpload, FaTrash } from 'react-icons/fa';

const API = import.meta.env.VITE_API_URL;

// Proper USD currency with comma separators
const USD = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});
const money = (v) => USD.format(Number.isFinite(+v) ? +v : 0);

// Pull logged-in user's full name (adjust to your auth shape)
function getUserFullName() {
  try {
    const u =
      JSON.parse(localStorage.getItem('currentUser')) ||
      JSON.parse(localStorage.getItem('user')) ||
      {};
    return u.fullname || u.userFullName || u.name || '';
  } catch {
    return '';
  }
}

const InvoiceDetail = () => {
  const { id } = useParams();

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [recomputing, setRecomputing] = useState(false);

  // history
  const [history, setHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);

  // Email modal state
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [emailTo, setEmailTo] = useState('');
  const [emailMessage, setEmailMessage] = useState('');
  const [emailing, setEmailing] = useState(false);
  const [emailAttachments, setEmailAttachments] = useState([]);
  const [emailStorageChoice, setEmailStorageChoice] = useState('detail');

  // Attachments state
  const [attachments, setAttachments] = useState([]);
  const [attachmentsLoading, setAttachmentsLoading] = useState(false);
  const [attachDesc, setAttachDesc] = useState('');
  const [pendingFiles, setPendingFiles] = useState([]);
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = React.useRef(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/invoices/${id}`);
      const json = await res.json();
      setData(json);
      const rawTo = json?.header?.billingemail || json?.header?.email || '';
      setEmailTo(String(rawTo || ''));
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, [id]);

  const fetchInvoiceAttachments = useCallback(async () => {
    const invoiceId = Number(id);
    if (!invoiceId) { setAttachments([]); return; }
    setAttachmentsLoading(true);
    try {
      const res = await fetch(`${API}/api/invoices/${invoiceId}/attachments`);
      const json = res.ok ? await res.json() : [];
      setAttachments(Array.isArray(json) ? json : []);
    } catch {
      setAttachments([]);
    } finally {
      setAttachmentsLoading(false);
    }
  }, [id]);

  const removePendingFile = (idx) => setPendingFiles(prev => prev.filter((_, i) => i !== idx));
  const onDrop = (e) => { e.preventDefault(); setIsDragging(false); const f = e.dataTransfer?.files; if (f && f.length) setPendingFiles(Array.from(f)); };
  const onDragOver = (e) => { e.preventDefault(); setIsDragging(true); };
  const onDragLeave = () => setIsDragging(false);

  const uploadFiles = async () => {
    const invoiceId = Number(id);
    if (!invoiceId || !pendingFiles.length) return;
    const fd = new FormData();
    pendingFiles.forEach(f => fd.append('files', f));
    if (attachDesc) fd.append('description', attachDesc);
    try {
      const res = await fetch(`${API}/api/invoices/${invoiceId}/attachments`, { method: 'POST', body: fd });
      if (!res.ok) throw new Error('Upload failed');
      setAttachDesc(''); setPendingFiles([]); await fetchInvoiceAttachments();
    } catch (e) {
      alert('Upload failed: ' + (e?.message || ''));
    }
  };

  const deleteAttachment = async (attId) => {
    const invoiceId = Number(id);
    if (!invoiceId || !attId) return;
    if (!window.confirm('Delete this attachment?')) return;
    try {
      await fetch(`${API}/api/invoices/${invoiceId}/attachments/${attId}`, { method: 'DELETE' });
      await fetchInvoiceAttachments();
    } catch (e) {
      alert('Delete failed: ' + (e?.message || ''));
    }
  };

  const loadHistory = useCallback(async () => {
    setHistoryLoading(true);
    try {
      const res = await fetch(`${API}/api/invoices/${id}/history`);
      if (!res.ok) throw new Error('Failed to fetch history');
      const json = await res.json();
      setHistory(Array.isArray(json) ? json : []);
    } catch (e) {
      console.error(e);
      setHistory([]);
    } finally {
      setHistoryLoading(false);
    }
  }, [id]);

  const recompute = async () => {
    setRecomputing(true);
    try {
      const res = await fetch(`${API}/api/invoices/${id}/recompute`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ userFullName: getUserFullName() }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.error || 'Failed to recompute');
      }
      await load();
      await loadHistory();
      alert('Recomputed & persisted.');
    } catch (e) {
      alert(e.message);
    } finally {
      setRecomputing(false);
    }
  };

  // Fetch attachments list when opening the email modal
  useEffect(() => {
    (async () => {
      if (!showEmailModal) return;
      try {
        const res = await fetch(`${API}/api/invoices/${id}/attachments`);
        const list = res.ok ? await res.json() : [];
        setEmailAttachments(Array.isArray(list) ? list : []);
      } catch { setEmailAttachments([]); }
    })();
  }, [showEmailModal, id]);

  const downloadPdf = () => { window.open(`${API}/api/invoices/${id}/pdf`, '_blank'); };

  const sendEmail = async () => {
    setEmailing(true);
    try {
      const res = await fetch(`${API}/api/invoices/${id}/email`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          to: emailTo,
          subject: `Invoice #${id}`,
          message: emailMessage,
          userFullName: getUserFullName(),
        }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.error || 'Email failed');
      }
      alert('Email sent!');
      await loadHistory();
      setShowEmailModal(false);
    } catch (e) {
      alert(e.message);
    } finally {
      setEmailing(false);
    }
  };

  // Close modal on ESC
  useEffect(() => {
    const handler = (e) => {
      if (e.key === 'Escape' && showEmailModal) {
        setShowEmailModal(false);
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [showEmailModal]);

  useEffect(() => {
    load();
    loadHistory();
    fetchInvoiceAttachments();
  }, [load, loadHistory, fetchInvoiceAttachments]);

  if (loading) return <div className="invoice-detail-wrapper">Loading...</div>;
  if (!data) return <div className="invoice-detail-wrapper">No data</div>;

  const { header, items, paymentsMerged = [], computed } = data;
  const hasPayments = Array.isArray(paymentsMerged) && paymentsMerged.length > 0;
  const receiptIds = Array.isArray(data.receiptIds) ? data.receiptIds : [];
  const orderIds   = Array.isArray(data.orderIds)   ? data.orderIds   : [];
  const eorderIds  = Array.isArray(data.eorderIds)  ? data.eorderIds  : [];

  const openAssoc = (type, docId) => {
    try {
      if (type === 'receipt') window.open(`${API}/api/receipts/${docId}/pdf`, '_blank', 'noopener,noreferrer');
      else if (type === 'order') window.open(`${API}/api/orders/${docId}/pdf`, '_blank', 'noopener,noreferrer');
      else if (type === 'eorder') window.open(`${API}/api/eorders/${docId}/packing-slip.pdf`, '_blank', 'noopener,noreferrer');
    } catch {}
  };

  return (
    <div className="invoice-detail-wrapper">
      <div className="header-row">
        <h1>Invoice #{header.id}</h1>
        <div className="actions">
          <Link className="btn" to="/invoices">
            <FaArrowLeft /> Back
          </Link>
          <button className="btn" onClick={() => setShowEmailModal(true)}>
            <FaEnvelope /> Email Invoice
          </button>
          <button className="btn" onClick={recompute} disabled={recomputing}>
            <FaSyncAlt /> {recomputing ? 'Recomputing…' : 'Recompute & Persist'}
          </button>
          <button className="btn" onClick={downloadPdf}>
            <FaFilePdf /> Download PDF
          </button>
        </div>
      </div>

      {/* Header info */}
      <section className="invoice-section">
        <div className="invoice-grid">
          <div><strong>Client:</strong> {header.clientName} (#{header.clientid})</div>
          <div><strong>Terms:</strong> {header.clientPaymentTerms || ''}</div>
          <div><strong>Date:</strong> {new Date(header.date).toLocaleString()}</div>
          <div><strong>Status:</strong> {header.statusName || header.status}</div>
          <div><strong>Order ID:</strong> {header.orderid || '-'}</div>
          <div><strong>Receipt ID:</strong> {header.receiptid || '-'}</div>
          <div><strong>eOrder ID:</strong> {header.eorderid || '-'}</div>
          <div className="col-span-2"><strong>Notes:</strong> {header.notes || ''}</div>
        </div>
      </section>

      {/* Associations */}
      {(receiptIds.length || orderIds.length || eorderIds.length) ? (
        <section className="invoice-section">
          <h2>Associations</h2>
          <div style={{ display:'flex', flexWrap:'wrap', gap:8 }}>
            {receiptIds.map(rid => (
              <span key={`wr-${rid}`} className="chip" title="Open WR PDF" onClick={() => openAssoc('receipt', rid)} style={{ cursor:'pointer' }}>
                WR #{rid}
              </span>
            ))}
            {orderIds.map(oid => (
              <span key={`ord-${oid}`} className="chip" title="Open Order PDF" onClick={() => openAssoc('order', oid)} style={{ cursor:'pointer' }}>
                Order #{oid}
              </span>
            ))}
            {eorderIds.map(eid => (
              <span key={`eo-${eid}`} className="chip" title="Open eOrder Packing Slip" onClick={() => openAssoc('eorder', eid)} style={{ cursor:'pointer' }}>
                eOrder #{eid}
              </span>
            ))}
          </div>
        </section>
      ) : null}

      {/* Computed (ALWAYS from merged totals) */}
      <section className="invoice-section">
        <div className="invoice-grid">
          <div><strong>Total:</strong> {money(computed.totalAmount)}</div>
          <div><strong>Paid:</strong> {money(computed.amountPaid)}</div>
          <div><strong>Balance:</strong> {money(computed.amountDue)}</div>
          {hasPayments && (
            <div><strong>Payments:</strong> {computed.paymentCount}</div>
          )}
          <div><strong>Last Payment:</strong> {computed.lastPaymentDate ? new Date(computed.lastPaymentDate).toLocaleString() : ''}</div>
        </div>
      </section>

      {/* Line Items */}
      <section className="invoice-section">
        <h2>Line Items</h2>
        <div className="table-wrapper">
          <table className="detail-table">
            <thead>
              <tr>
                <th>Charge Name</th>
                <th>Receive Date</th>
                <th>Warehouse</th>
                <th>Description</th>
                <th>Qty</th>
                <th>Rate (amt)</th>
                <th>Extended</th>
              </tr>
            </thead>
            <tbody>
              {items.length ? items.map(li => {
                const qty = Number(li.qty) || 0;
                const rate = Number(li.amt) || 0;
                const extended = Math.round(qty * rate * 100) / 100;
                const whText = (li.warehouseName && String(li.warehouseName)) || ((li.warehouseid && Number(li.warehouseid) > 0) ? String(li.warehouseid) : '');
                const recDateText = li.docDate ? new Date(li.docDate).toLocaleDateString() : '';
                return (
                  <tr key={li.id}>
                    <td>{li.chargename}</td>
                    <td>{recDateText}</td>
                    <td>{whText}</td>
                    <td>{li.description ?? ''}</td>
                    <td>{qty.toFixed(2)}</td>
                    <td>{rate.toFixed(3)}</td>
                    <td>{money(extended)}</td>
                  </tr>
                );
              }) : (
                <tr><td colSpan="7">No items.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </section>

      {/* Payments (merged) */}
      {hasPayments && (
        <section className="invoice-section">
          <h2>Payments</h2>
          <div className="table-wrapper">
            <table className="detail-table">
              <thead>
                <tr>
                  <th>ID</th>
                  <th>Date</th>
                  <th>Type</th>
                  <th>Applied Amt</th>
                  <th>Original Amt</th>
                  <th>Reference</th>
                  <th>User</th>
                  <th>Notes</th>
                </tr>
              </thead>
              <tbody>
                {paymentsMerged.map(p => (
                  <tr key={p.id}>
                    <td>{p.id}</td>
                    <td>{new Date(p.date).toLocaleString()}</td>
                    <td>{p.paymentTypeName || p.paymenttype}</td>
                    <td>{money(p.appliedAmt)}</td>
                    <td>{money(p.amt)}</td>
                    <td>{p.reference || ''}</td>
                    <td>{p.user || ''}</td>
                    <td>{p.notes || ''}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}

  {/* History */}
  <section className="invoice-section">
        <h2>History</h2>
        {historyLoading ? (
          <div className="loading">Loading history…</div>
        ) : (
          <div className="table-wrapper">
            <table className="detail-table">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>User</th>
                  <th>Log</th>
                </tr>
              </thead>
              <tbody>
                {history.length ? history.map(h => (
                  <tr key={h.id}>
                    <td>{new Date(h.date).toLocaleString()}</td>
                    <td>{h.userFullName || ''}</td>
                    <td>{h.log}</td>
                  </tr>
                )) : (
                  <tr><td colSpan="3">No history.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        )}
  </section>

      {/* Attachments */}
      <section className="invoice-section">
        <h2 style={{ display:'flex', alignItems:'center', gap:8 }}>
          <FaPaperclip /> Attachments
        </h2>
        <div
          className={`attachments-drop ${isDragging ? 'dragging' : ''}`}
          onDrop={onDrop}
          onDragOver={onDragOver}
          onDragLeave={onDragLeave}
          style={{
            padding: 12,
            border: '1px dashed #aaa',
            borderRadius: 6,
            marginBottom: 12,
            background: isDragging ? 'rgba(0,0,0,0.05)' : 'transparent'
          }}
          aria-label="Drop files here"
        >
          <div className="attachments-toolbar" style={{ display:'flex', gap:8, alignItems:'center', flexWrap:'wrap' }}>
            <input
              type="text"
              placeholder="Description (optional)"
              value={attachDesc}
              onChange={e => setAttachDesc(e.target.value)}
              style={{ flex:'1 1 240px', minWidth: 220 }}
            />
            <input
              ref={fileInputRef}
              type="file"
              multiple
              onChange={(e) => setPendingFiles(Array.from(e.target.files || []))}
              style={{ display:'none' }}
            />
            <button className="btn" type="button" onClick={() => fileInputRef.current?.click?.()} title="Choose files to upload">
              <FaPaperclip style={{ verticalAlign:'-2px' }} /> Choose Files
            </button>
            <button className="btn" type="button" onClick={uploadFiles} disabled={!pendingFiles.length} title="Upload selected files">
              <FaUpload style={{ verticalAlign:'-2px' }} /> Upload
            </button>
          </div>

          {!!pendingFiles.length && (
            <div className="pending-files" style={{ marginTop: 8 }}>
              {pendingFiles.map((f, i) => (
                <span key={f.name + i} className="file-chip" style={{ display:'inline-flex', alignItems:'center', gap:6, padding:'2px 6px', border:'1px solid #ddd', borderRadius: 12, marginRight:6, marginBottom:6 }}>
                  {f.name}
                  <button type="button" className="chip-x" title="Remove" aria-label={`Remove ${f.name}`} onClick={() => removePendingFile(i)} style={{ border:'none', background:'transparent', cursor:'pointer' }}>×</button>
                </span>
              ))}
            </div>
          )}
        </div>

        <div className="table-wrapper">
          <table className="detail-table">
            <thead>
              <tr>
                <th style={{ width: 72 }}>Action</th>
                <th>Filename</th>
                <th>Date</th>
                <th>Description</th>
              </tr>
            </thead>
            <tbody>
              {attachmentsLoading ? (
                <tr><td colSpan={4}>Loading…</td></tr>
              ) : attachments.length ? (
                attachments.map(a => {
                  const name = (a.filename || '').split('/').pop();
                  return (
                    <tr key={a.id ?? name}>
                      <td>
                        {a.id ? (
                          <button className="mini-btn danger" title="Delete attachment" onClick={() => deleteAttachment(a.id)}>
                            <FaTrash />
                          </button>
                        ) : null}
                      </td>
                      <td>
                        <a href={`${API}/api/invoices/${id}/attachments/${encodeURIComponent(name)}`} target="_blank" rel="noopener noreferrer">{name}</a>
                      </td>
                      <td>{a.date ? new Date(a.date).toLocaleString() : ''}</td>
                      <td>{a.description || ''}</td>
                    </tr>
                  );
                })
              ) : (
                <tr><td colSpan={4} style={{ color:'#999' }}>No attachments</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </section>

      {/* EMAIL MODAL */}
      {showEmailModal && (
        <EmailModal
          open={showEmailModal}
          title={`Email Invoice #${header.id}`}
          initialTo={emailTo}
          initialSubject={`Invoice #${header.id}`}
          initialMessage={emailMessage}
          allowCcBcc={true}
          ccPlaceholder=""
          bccPlaceholder=""
          toName="invoice_email_to"
          ccName="invoice_email_cc"
          bccName="invoice_email_bcc"
          toAutoComplete="email"
          ccAutoComplete="email"
          bccAutoComplete="email"
          attachments={emailAttachments}
          extraContent={(function(){
            const isStorage = String(header?.invoiceTypeName || header?.type || '').toLowerCase().includes('storage');
            if (!isStorage) return null;
            return (
              <div style={{ marginTop: 8 }}>
                <strong>Storage Invoice Options</strong>
                <div style={{ display:'flex', gap:16, marginTop:6 }}>
                  <label style={{ display:'inline-flex', alignItems:'center', gap:6 }}>
                    <input type="radio" name="storEmailDetail" checked={emailStorageChoice==='summary'} onChange={()=>setEmailStorageChoice('summary')} /> Summarize charges
                  </label>
                  <label style={{ display:'inline-flex', alignItems:'center', gap:6 }}>
                    <input type="radio" name="storEmailDetail" checked={emailStorageChoice==='detail'} onChange={()=>setEmailStorageChoice('detail')} /> Show details
                  </label>
                </div>
              </div>
            );
          })()}
          onClose={() => setShowEmailModal(false)}
          onSend={async ({ to, cc, bcc, subject, message, attachmentsSelected }) => {
            const res = await fetch(`${API}/api/invoices/${id}/email`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                to, cc, bcc, subject, message,
                userFullName: (JSON.parse(localStorage.getItem('currentUser')||'{}').fullname)
                  || (JSON.parse(localStorage.getItem('user')||'{}').fullname)
                  || '',
                selectedAttachmentNames: Array.isArray(attachmentsSelected) ? attachmentsSelected : [],
                storageSummary: String(header?.invoiceTypeName || header?.type || '').toLowerCase().includes('storage') ? (emailStorageChoice==='summary') : undefined,
              }),
            });
            if (!res.ok) {
              const err = await res.json().catch(() => ({}));
              throw new Error(err.error || 'Email failed');
            }
            alert('Email sent!');
            await loadHistory();
          }}
        />

      )}
    </div>
  );
};

export default InvoiceDetail;
